import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Box, Button, Paper, TextField, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router-dom';
import {
  OLD_SIM_FATES,
  OLD_SIM_FATE_LABEL,
  PORTIN_REQUEST_MODES,
  PORTIN_TYPES,
  SWAP_TYPES,
  SWAP_TYPE_LABEL,
  US_STATES,
  portInSchema,
} from '@app/shared';
import type { PortInDetailDto, PortInInput, PortSubscriberDto } from '@app/shared';
import { LoginIdField } from '../../components/LoginIdSuggest';
import { useNotify } from '../../components/NotifyProvider';
import { PageHeader } from '../../components/PageHeader';
import { SelectInput, TextInput, applyServerErrors, toOptions } from '../../components/form';
import { DataTable } from '../../components/DataTable';
import { ReleasedFeature, useFeatureReleased } from '../../release';
import { api, errorMessage } from '../../lib/api';
import { withRequiredMessages } from '../inventory/formUtils';

type PortForm = Omit<PortInInput, 'portinType' | 'state'> & {
  portinType: PortInInput['portinType'] | '';
  state: string;
};

const STATE_OPTIONS = US_STATES.map(([code, name]) => ({
  value: code,
  label: `${code} - ${name}`,
}));

/** Who got it back, IMEI and Plan name of the reference form. */
const WHO_FEATURE = 'portin-who-got-it-back';

function PortForm({ subscriber, onDone }: { subscriber: PortSubscriberDto; onDone: () => void }) {
  const notify = useNotify();
  const whoOn = useFeatureReleased()(WHO_FEATURE);
  const [check, setCheck] = useState<{ portable: boolean; message: string } | null>(null);
  const { control, handleSubmit, setError, getValues } = useForm<PortForm>({
    defaultValues: {
      customerId: subscriber.id,
      portinType: '',
      newSim: '',
      oldSimFate: OLD_SIM_FATES[0],
      mdn: '',
      oldCarrier: '',
      accountNumber: '',
      passwordPin: '',
      billFirstName: '',
      billLastName: '',
      address1: subscriber.address.line1,
      address2: subscriber.address.line2,
      city: subscriber.address.city,
      state: subscriber.address.state,
      zip: subscriber.address.zip,
      swapType: SWAP_TYPES[0],
      requestMode: 'Automatic',
      returnedTo: '',
      imei: '',
    },
    resolver: withRequiredMessages<PortForm>(
      zodResolver(portInSchema) as unknown as Resolver<PortForm>,
      { portinType: 'Select Port-in Type', state: 'State is required' },
    ),
  });
  const portinType = useWatch({ control, name: 'portinType' });
  const oldSimFate = useWatch({ control, name: 'oldSimFate' });
  const newSim = portinType === 'Port-in with New SIM';
  const asksWho = whoOn && newSim && oldSimFate === 'Make the old SIM available';

  const portability = useMutation({
    mutationFn: (mdn: string) =>
      api.post<{ portable: boolean; message: string }>('/ports/portability', {
        mdn,
        carrierId: subscriber.carrier.id,
      }),
  });
  const create = useMutation({
    mutationFn: (input: PortForm) => api.post<PortInDetailDto>('/ports/in', input),
  });
  const submit = handleSubmit(async (values) => {
    try {
      const { returnedTo, imei, ...rest } = values;
      const done = await create.mutateAsync({
        ...rest,
        newSim: newSim ? values.newSim : '',
        ...(whoOn ? { returnedTo: asksWho ? returnedTo : '', imei } : {}),
      });
      notify.success(
        done.status === 'Failed'
          ? `The carrier refused the request: ${done.carrierResponse}`
          : `Port-in of ${done.mdn} requested.`,
      );
      onDone();
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });

  return (
    <Paper
      variant="outlined"
      component="form"
      noValidate
      aria-label="Existing Subscriber Portin"
      onSubmit={(e) => void submit(e)}
      sx={{ p: 2, maxWidth: 1040 }}
    >
      <Box
        component="dl"
        aria-label="Subscriber"
        sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 3, rowGap: 0.5, m: 0, mb: 2 }}
      >
        {(
          [
            ['Customer', `${subscriber.name} (${subscriber.customerId})`],
            ['Carrier', subscriber.carrier.code],
            ['Phone number', subscriber.mdn],
            ['SIM', subscriber.sim],
            ['Status', subscriber.status],
            ...(whoOn && subscriber.planLabel
              ? [['Plan name', subscriber.planLabel] as const]
              : []),
          ] as const
        ).map(([label, value]) => (
          <Box key={label} sx={{ display: 'flex', gap: 0.75 }}>
            <Typography component="dt" sx={{ fontWeight: 600 }}>
              {label}:
            </Typography>
            <Typography component="dd" sx={{ m: 0 }}>
              {value}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))',
          alignItems: 'start',
          mb: 2,
        }}
      >
        <SelectInput
          control={control}
          name="portinType"
          label="Port-in Type"
          required
          emptyLabel="Select"
          options={toOptions(PORTIN_TYPES)}
        />
        {newSim ? (
          <>
            <TextInput
              control={control}
              name="newSim"
              label="New SIM"
              required
              autoComplete="off"
              slotProps={{ htmlInput: { maxLength: 22, inputMode: 'numeric' } }}
            />
            <SelectInput
              control={control}
              name="oldSimFate"
              label="Old SIM status"
              options={OLD_SIM_FATES.map((v) => ({ value: v, label: OLD_SIM_FATE_LABEL[v] }))}
            />
            {asksWho ? (
              <Controller
                control={control}
                name="returnedTo"
                render={({ field, fieldState }) => (
                  <LoginIdField
                    label="Who got it back(Employee LoginID)"
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    error={fieldState.error?.message}
                  />
                )}
              />
            ) : null}
          </>
        ) : null}
        {whoOn ? (
          <TextInput
            control={control}
            name="imei"
            label="IMEI"
            autoComplete="off"
            slotProps={{ htmlInput: { maxLength: 30 } }}
          />
        ) : null}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <TextInput
            control={control}
            name="mdn"
            label="Number to port"
            required
            autoComplete="off"
            slotProps={{ htmlInput: { maxLength: 10, inputMode: 'numeric' } }}
          />
          <Button
            variant="outlined"
            disabled={portability.isPending}
            onClick={() => {
              setCheck(null);
              void portability
                .mutateAsync(getValues('mdn'))
                .then(setCheck)
                .catch((err: unknown) => setCheck({ portable: false, message: errorMessage(err) }));
            }}
            sx={{ whiteSpace: 'nowrap', mt: 0.25 }}
          >
            Check Portability
          </Button>
        </Box>
        <TextInput
          control={control}
          name="oldCarrier"
          label="New Carrier"
          helperText="The provider the number comes from"
          slotProps={{ htmlInput: { maxLength: 60 } }}
        />
        <TextInput
          control={control}
          name="accountNumber"
          label="Account number"
          required
          autoComplete="off"
          slotProps={{ htmlInput: { maxLength: 40 } }}
        />
        <TextInput
          control={control}
          name="passwordPin"
          label="Password PIN"
          type="password"
          autoComplete="off"
          helperText="Sent to the carrier; not stored"
          slotProps={{ htmlInput: { maxLength: 20 } }}
        />
        <TextInput control={control} name="billFirstName" label="Bill First name" required />
        <TextInput control={control} name="billLastName" label="Bill last name" required />
        <ReleasedFeature name="portin-address-fields">
          <TextInput
            control={control}
            name="businessName"
            label="Business name"
            slotProps={{ htmlInput: { maxLength: 60 } }}
          />
          <TextInput
            control={control}
            name="newNumLocalSp"
            label="New num local SP"
            slotProps={{ htmlInput: { maxLength: 15 } }}
          />
          <TextInput
            control={control}
            name="streetNumber"
            label="Street number"
            slotProps={{ htmlInput: { maxLength: 20 } }}
          />
          <TextInput
            control={control}
            name="streetType"
            label="Street type"
            slotProps={{ htmlInput: { maxLength: 30 } }}
          />
          <TextInput
            control={control}
            name="streetDirection"
            label="Street direction"
            slotProps={{ htmlInput: { maxLength: 30 } }}
          />
        </ReleasedFeature>
        <TextInput control={control} name="address1" label="Address 1" required />
        <TextInput control={control} name="address2" label="Address 2" />
        <TextInput control={control} name="city" label="City" required />
        <SelectInput
          control={control}
          name="state"
          label="State"
          required
          emptyLabel="Select"
          options={STATE_OPTIONS}
        />
        <TextInput
          control={control}
          name="zip"
          label="ZIP"
          required
          slotProps={{ htmlInput: { maxLength: 5, inputMode: 'numeric' } }}
        />
        <SelectInput
          control={control}
          name="swapType"
          label="Swap type"
          options={SWAP_TYPES.map((v) => ({ value: v, label: SWAP_TYPE_LABEL[v] }))}
        />
        <SelectInput
          control={control}
          name="requestMode"
          label="Portin Request Type"
          options={toOptions(PORTIN_REQUEST_MODES)}
          helperText="Manual requests wait in Portin Queue(Manual)"
        />
      </Box>
      {check ? (
        <Alert severity={check.portable ? 'success' : 'warning'} sx={{ mb: 2 }}>
          {check.message}
        </Alert>
      ) : null}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button type="submit" variant="contained" disabled={create.isPending}>
          Submit
        </Button>
        <Button onClick={onDone} disabled={create.isPending}>
          Exit without changes
        </Button>
      </Box>
    </Paper>
  );
}

/**
 * Existing Subscriber Portin (PC343): find a customer, then bring a number from another
 * provider onto their line. The reference form also asks for an SSN and street parts; the
 * SSN is deliberately not collected here.
 */
/** Release switch of the reference's search by name, enrollment ID or customer ID. */
const SEARCH_FEATURE = 'portin-subscriber-search';

export function ExistingSubscriberPortinPage() {
  const [q, setQ] = useState('');
  const [subscriber, setSubscriber] = useState<PortSubscriberDto | null>(null);
  const [problem, setProblem] = useState('');
  const [matches, setMatches] = useState<PortSubscriberDto[] | null>(null);
  const featureOn = useFeatureReleased();
  const wide = featureOn(SEARCH_FEATURE);
  const find = useMutation({
    mutationFn: (value: string) => api.get<PortSubscriberDto>('/ports/subscriber', { q: value }),
  });
  const searchMany = useMutation({
    mutationFn: (value: string) =>
      api
        .get<{ data: PortSubscriberDto[] }>('/ports/subscriber-search', { q: value })
        .then((r) => r.data),
  });
  const lookUp = (value: string) => {
    setProblem('');
    setSubscriber(null);
    void find
      .mutateAsync(value.trim())
      .then(setSubscriber)
      .catch((err: unknown) => setProblem(errorMessage(err)));
  };
  const search = (e: FormEvent) => {
    e.preventDefault();
    if (!wide) return lookUp(q);
    // One customer opens the form at once; several are listed to choose from.
    setProblem('');
    setSubscriber(null);
    setMatches(null);
    void searchMany
      .mutateAsync(q.trim())
      .then((found) => {
        if (found.length === 1) setSubscriber(found[0]!);
        else if (found.length === 0) setProblem('No customer found.');
        else setMatches(found);
      })
      .catch((err: unknown) => setProblem(errorMessage(err)));
  };
  // Opened from a customer's Quick Links: that customer is looked up straight away.
  const [params] = useSearchParams();
  const preset = params.get('customer') ?? '';
  useEffect(() => {
    if (!/^\d{1,11}$/.test(preset)) return;
    setQ(preset);
    lookUp(preset);
    // Runs once per customer in the address; `lookUp` is not stable between renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset]);

  return (
    <Box>
      <PageHeader
        title="Existing Subscriber Portin"
        code="PC343"
        actions={
          <Button component={Link} to="/ports/in/pending" variant="outlined">
            Pending Portin
          </Button>
        }
      />
      <Paper
        variant="outlined"
        component="form"
        aria-label="Find subscriber"
        onSubmit={search}
        sx={{ p: 1.5, mb: 1.5, display: 'flex', gap: 1.5, alignItems: 'flex-start' }}
      >
        <TextField
          label={
            wide ? 'Search by customer name, enrollment id, Customer id' : 'Customer ID or MDN'
          }
          value={q}
          onChange={(e) => setQ(e.target.value)}
          error={Boolean(problem)}
          helperText={problem || undefined}
          slotProps={{
            htmlInput: wide ? { maxLength: 60 } : { maxLength: 11, inputMode: 'numeric' },
          }}
          sx={{ width: wide ? 380 : 260 }}
        />
        <Button
          type="submit"
          variant="contained"
          disabled={find.isPending || searchMany.isPending || q.trim() === ''}
        >
          Search
        </Button>
      </Paper>
      {matches && !subscriber ? (
        <Box sx={{ mb: 1.5 }}>
          <DataTable
            label="Customers found"
            columns={[
              { key: 'customerId', header: 'Customer ID', render: (m) => m.customerId },
              { key: 'enrollmentId', header: 'Enrollment ID', render: (m) => m.enrollmentId },
              { key: 'name', header: 'Name', render: (m) => m.name },
              { key: 'mdn', header: 'MDN', render: (m) => m.mdn },
              { key: 'status', header: 'Status', render: (m) => m.status },
              {
                key: 'action',
                header: 'Action',
                render: (m) => (
                  <Button size="small" onClick={() => setSubscriber(m)}>
                    Create new portin request
                  </Button>
                ),
              },
            ]}
            rows={matches}
            rowKey={(m) => m.id}
          />
          {matches.length === 20 ? (
            <Typography color="text.secondary" sx={{ fontSize: 13, mt: 0.5 }}>
              The first 20 customers are shown; type more of the name to narrow the search.
            </Typography>
          ) : null}
        </Box>
      ) : null}
      {subscriber ? (
        // `key` gives each subscriber a fresh form.
        <PortForm
          key={subscriber.id}
          subscriber={subscriber}
          onDone={() => {
            setSubscriber(null);
            setMatches(null);
            setQ('');
          }}
        />
      ) : null}
    </Box>
  );
}
